/**
 * HALO - Wellness Trend Chart
 * Interactive area chart showing team wellness trends.
 * Now accepts data as props from the parent component.
 */

import { useState, useEffect } from "react";
import { TrendingUp } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardContent,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { WellnessChartDataPoint } from "@/lib/supabase";

// ============================================================================
// TIME RANGE TOGGLE
// ============================================================================

const timeRanges = [
  { label: "7 days", value: "7d", days: 7 },
  { label: "30 days", value: "30d", days: 30 },
  { label: "90 days", value: "90d", days: 90 },
];

// ============================================================================
// CUSTOM TOOLTIP
// ============================================================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-surface-dark border border-glass-grey rounded-xl p-3 shadow-lg text-xs">
      <p className="text-muted-slate mb-2 font-medium">
        {new Date(label).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })}
      </p>
      {payload.map((entry: any, idx: number) => (
        <div key={idx} className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-slate">{entry.name}</span>
          </div>
          <span className="text-foreground font-mono font-medium">
            {entry.name === "Avg Fatigue"
              ? `${entry.value.toFixed(0)}%`
              : entry.name === "Wellness Score"
              ? `${entry.value}%`
              : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// CHART COMPONENT
// ============================================================================

interface WellnessChartProps {
  data?: WellnessChartDataPoint[];
  onTimeRangeChange?: (days: number) => void;
}

export function WellnessChart({
  data = [],
  onTimeRangeChange,
}: WellnessChartProps) {
  const [timeRange, setTimeRange] = useState("30d");

  // Filter data based on selected time range
  const filteredData = data.filter((item) => {
    const date = new Date(item.date);
    const now = new Date();
    const range = timeRanges.find((r) => r.value === timeRange);
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - (range?.days || 30));
    return date >= cutoff;
  });

  // Notify parent when time range changes so it can fetch the right data
  useEffect(() => {
    const range = timeRanges.find((r) => r.value === timeRange);
    if (range && onTimeRangeChange) {
      onTimeRangeChange(range.days);
    }
  }, [timeRange, onTimeRangeChange]);

  const hasData = data.length > 0;

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Team Wellness Trends</CardTitle>
          <CardDescription>
            Average posture score, fatigue index, and wellness score across the
            team
          </CardDescription>
        </div>
        <CardAction>
          <div className="flex items-center rounded-xl border border-glass-grey overflow-hidden">
            {timeRanges.map((range) => (
              <button
                key={range.value}
                onClick={() => setTimeRange(range.value)}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium transition-colors",
                  timeRange === range.value
                    ? "bg-primary text-white"
                    : "text-muted-slate hover:text-foreground hover:bg-surface-elevated",
                )}
              >
                {range.label}
              </button>
            ))}
          </div>
        </CardAction>
      </CardHeader>
      <CardContent className="pt-4">
        {hasData ? (
          <>
            <div className="h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={filteredData}
                  margin={{ top: 5, right: 5, bottom: 5, left: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="fillPosture"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="oklch(0.65 0.18 230)"
                        stopOpacity={0.4}
                      />
                      <stop
                        offset="95%"
                        stopColor="oklch(0.65 0.18 230)"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                    <linearGradient
                      id="fillFatigue"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="oklch(0.72 0.16 75)"
                        stopOpacity={0.4}
                      />
                      <stop
                        offset="95%"
                        stopColor="oklch(0.72 0.16 75)"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                    <linearGradient
                      id="fillWellness"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="oklch(0.70 0.18 150)"
                        stopOpacity={0.4}
                      />
                      <stop
                        offset="95%"
                        stopColor="oklch(0.70 0.18 150)"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke="oklch(0.28 0 0)"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={32}
                    tick={{ fill: "oklch(0.45 0 0)", fontSize: 11 }}
                    tickFormatter={(value) =>
                      new Date(value).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: "oklch(0.45 0 0)", fontSize: 11 }}
                    domain={[0, 100]}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Area
                    name="Wellness Score"
                    dataKey="avgWellness"
                    type="monotone"
                    fill="url(#fillWellness)"
                    stroke="oklch(0.70 0.18 150)"
                    strokeWidth={2}
                  />
                  <Area
                    name="Avg Posture"
                    dataKey="avgPosture"
                    type="monotone"
                    fill="url(#fillPosture)"
                    stroke="oklch(0.65 0.18 230)"
                    strokeWidth={2}
                  />
                  <Area
                    name="Avg Fatigue"
                    dataKey={(d: any) => Math.round(d.avgFatigue * 100)}
                    type="monotone"
                    fill="url(#fillFatigue)"
                    stroke="oklch(0.72 0.16 75)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-6 mt-4 px-2">
              <div className="flex items-center gap-2 text-xs text-muted-slate">
                <div className="w-3 h-0.5 rounded bg-signal-green" />
                Wellness Score
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-slate">
                <div className="w-3 h-0.5 rounded bg-signal-blue" />
                Avg Posture Score
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-slate">
                <div className="w-3 h-0.5 rounded bg-signal-amber" />
                Avg Fatigue Index (%)
              </div>
            </div>
          </>
        ) : (
          <div className="h-[280px] w-full flex flex-col items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-surface-elevated flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-muted-slate/40" />
            </div>
            <p className="text-sm text-muted-slate">
              No wellness data available
            </p>
            <p className="text-xs text-muted-slate/60 mt-1">
              Data will appear here when team members start monitoring
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
